import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { listCurrentFoodTypes } from '../../pets';
import { PetModel } from '../../pets/models/PetModel';
import { DraftState } from '../hooks/useDraft';
import {
  QuickSalePlanModel,
  QuickSaleUserModel,
} from '../models/QuickSaleModel';
import { DailyRationField } from './DailyRationField';
import { FieldRow, QuickSaleCard } from './QuickSaleCard';

type Props = {
  user: QuickSaleUserModel;
  savedPet: PetModel;
  plan: QuickSalePlanModel | null;
  savedPlan: QuickSalePlanModel | null;
  draftState: DraftState;
};

/** Adaptation: how long it lasts, what the pet eats today and the guide. */
const TransitionCard: FC<Props> = ({
  user,
  savedPet,
  plan,
  savedPlan,
  draftState,
}) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [foodTypes, setFoodTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const { draft, update } = draftState;
  const petKindId = savedPet.pet_kind?.id;
  const foodTypeId =
    draft.pet?.current_food_type ?? savedPet.current_food_type?.id;
  const multiplier =
    draft.pet?.customer_multiplier ?? savedPet.customer_multiplier ?? 0;

  useEffect(() => {
    if (!editing || !petKindId) {
      return;
    }
    listCurrentFoodTypes({ pet_kind: petKindId })
      .then(({ data }) => setFoodTypes(data))
      .catch((error) => console.warn(error));
  }, [editing, petKindId]);

  const foodTypeName =
    foodTypes.find((foodType) => foodType.id === foodTypeId)?.name ||
    savedPet.current_food_type?.name ||
    '-';

  return (
    <QuickSaleCard
      icon="fa-leaf"
      title={intl.formatMessage({ id: 'quickSale.transition' })}
      editing={editing}
      onToggleEdit={() => setEditing(!editing)}
      className="h-100"
    >
      <FieldRow label={intl.formatMessage({ id: 'quickSale.duration' })}>
        <i className="far fa-calendar-alt me-2 text-gray-500" />
        {intl.formatMessage(
          { id: 'quickSale.days' },
          { days: user.store.transition_days }
        )}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.currentFood' })}
        changed={draft.pet?.current_food_type !== undefined}
      >
        {editing ? (
          <select
            className="form-select form-select-sm"
            value={foodTypeId ?? ''}
            aria-label={intl.formatMessage({ id: 'quickSale.currentFood' })}
            onChange={(event) => {
              const value = Number(event.target.value);
              update('pet', {
                current_food_type:
                  value === savedPet.current_food_type?.id ? undefined : value,
              });
            }}
          >
            {foodTypes.map((foodType) => (
              <option key={foodType.id} value={foodType.id}>
                {foodType.name}
              </option>
            ))}
          </select>
        ) : (
          foodTypeName
        )}
      </FieldRow>
      <FieldRow label={intl.formatMessage({ id: 'quickSale.transitionGuide' })}>
        {plan?.instructions_url ? (
          <a
            href={plan.instructions_url}
            target="_blank"
            rel="noreferrer"
            className="link"
          >
            <i className="fas fa-link me-2" />
            {intl.formatMessage({ id: 'quickSale.seeTransitionGuide' })}
          </a>
        ) : (
          <span className="text-muted">
            {intl.formatMessage({ id: 'quickSale.noTransitionGuide' })}
          </span>
        )}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.dailyRation' })}
        changed={draft.pet?.customer_multiplier !== undefined}
      >
        <DailyRationField
          variant="inline"
          grams={plan?.average_grams_per_day ?? null}
          calories={null}
          baseGrams={savedPlan?.average_grams_per_day ?? null}
          baseMultiplier={savedPet.customer_multiplier || 0}
          multiplier={multiplier}
          changed={draft.pet?.customer_multiplier !== undefined}
          onChange={(value) =>
            update('pet', {
              customer_multiplier:
                value === (savedPet.customer_multiplier || 0)
                  ? undefined
                  : value,
            })
          }
        />
      </FieldRow>
    </QuickSaleCard>
  );
};

export { TransitionCard };
